// ============================================
// HLX-PRO.10 — Protocol Stripping & Handoff Types
//
// Transforms validated protocol into checkout-safe
// output. Strips all clinical data.
//
// LOCKED CONSTRAINTS:
// - Allow-list construction ONLY
// - Clinical data NEVER reaches checkout
// - Gate 2 requires PROVIDER_APPROVED
// - No default-to-1 for quantity
// - No default-to-90 for duration
// - Duration 1-365 days bounds
// ============================================

import type { FormularyGap } from './pipelineProvenance';

// ── Stripped Protocol (checkout-safe) ──

export interface StrippedProtocol {
  protocolId: string;
  products: StrippedProduct[];
  /** Consent timestamps — accepts plain strings or structured ConsentTimestamp objects */
  consentTimestamps: Array<string | ConsentTimestamp>;
  clinicalDataStripped: true; // Literal true — compile-time gate
  strippedAt: string;
  /** PRO.10 amendment: Formulary gaps from provenance — biomarkers with no product match */
  formularyGaps?: FormularyGap[];
}

/** PRO.10 #7: Structured consent timestamp with type and version */
export interface ConsentTimestamp {
  /** ISO 8601 timestamp */
  timestamp: string;
  /** PRO.10 #7: Type of consent (e.g., 'checkout', 'protocol_review') */
  consentType?: string;
  /** PRO.10 #7: Version of consent form */
  consentVersion?: string;
}

export interface StrippedProduct {
  productId: string;
  name: string;
  quantity: number;
  durationDays: number;
}

// ── Stripping Input ──

export interface StrippingInput {
  protocolId: string;
  /** Supplements from validated protocol */
  supplements: Array<{
    productId: string;
    name: string;
    dosage: string;
    timing: string;
    rationale: string;
  }>;
  /** Gate classification */
  gate: 'GATE_1' | 'GATE_2';
  /** Approval status */
  approvalStatus: string;
  /** Consent timestamps */
  consentTimestamps: string[];
  /** Product details for quantity derivation */
  productCatalog: Map<string, {
    skuUnitSize?: string;
    typicalQuantity?: number;
    defaultDurationDays?: number;
  }>;
  /** Duration string from protocol (e.g., "90 days", "3 months") */
  durationString?: string;
  /** PRO.10 amendment: Formulary gaps from SessionProvenance — threaded through from caller */
  formularyGaps?: FormularyGap[];
}

// ── Stripping Error ──

export type StrippingErrorCode =
  | 'GATE_2_NOT_APPROVED'
  | 'EMPTY_PRODUCTS'
  | 'EMPTY_CONSENT'
  | 'QUANTITY_UNPARSEABLE'
  | 'DURATION_UNPARSEABLE'
  | 'DURATION_OUT_OF_BOUNDS'
  | 'SUSPICIOUS_QUANTITY';

// ── PRO.10 #4: Stripping Audit Event ──

export interface StrippingAuditEvent {
  protocolId: string;
  strippedAt: string;
  productsStripped: number;
  fieldsRemoved: string[];
  clinicalDataStripped: true;
}

// ── PRO.10 #10: Compile-time allowed fields assertion ──

/** Fields allowed through stripping — compile-time keyof check */
export const SUPPLEMENT_ALLOWED_FIELDS: readonly (keyof StrippedProduct)[] = [
  'productId',
  'name',
  'quantity',
  'durationDays',
] as const;

// ── Constants ──

export const MIN_DURATION_DAYS = 1;
export const MAX_DURATION_DAYS = 365;
export const SUSPICIOUS_QUANTITY_MULTIPLIER = 4;
